import React, { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import './AssessmentDetailsChart.css';

interface QuestionAnswer {
  questionId: string;
  questionText: string;
  questionType: string;
  questionValue: number;
  answerData: any;
  answerScore: number;
  answeredAt?: string;
  isAnswered: boolean;
  questionOptions?: { id: string; en_value: string; ar_value?: string; value?: number }[] | string;
}

interface CategoryData {
  categoryId: string;
  categoryName: string;
  questions: QuestionAnswer[];
}

interface AssessmentDetailsChartProps {
  data: {
    categories: CategoryData[];
  };
}

const AssessmentDetailsChart: React.FC<AssessmentDetailsChartProps> = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const formatAnswer = (answerData: any, questionType: string, questionOptions?: { id: string; en_value: string; ar_value?: string; value?: number }[] | string): string => {
    if (!answerData && answerData !== false && answerData !== 0) return 'Not answered';

    // Parse options if they come as a JSON string
    let parsedOptions: { id: string; en_value: string; ar_value?: string; value?: number }[] | undefined;
    if (typeof questionOptions === 'string') {
      try {
        parsedOptions = JSON.parse(questionOptions);
      } catch {
        parsedOptions = undefined;
      }
    } else {
      parsedOptions = questionOptions;
    }

    // Helper function to find option by ID (handles string comparison)
    const findOptionById = (id: any, options?: { id: string; en_value: string; ar_value?: string; value?: number }[]): { id: string; en_value: string; ar_value?: string; value?: number } | undefined => {
      if (!options || !id || !Array.isArray(options)) return undefined;
      const idStr = String(id).toLowerCase().trim();
      return options.find((opt: any) => {
        const optIdStr = String(opt.id || '').toLowerCase().trim();
        return optIdStr === idStr;
      });
    };

    switch (questionType) {
      case 'yes_no':
        if (typeof answerData === 'boolean') {
          return answerData ? 'Yes' : 'No';
        }
        if (typeof answerData === 'string' && parsedOptions) {
          const option = findOptionById(answerData, parsedOptions);
          if (option) {
            return option.en_value || option.ar_value || answerData;
          }
          // Try to match by value text
          const textMatch = parsedOptions.find((opt: any) => 
            opt.en_value?.toLowerCase().includes(answerData.toLowerCase()) ||
            opt.ar_value?.toLowerCase().includes(answerData.toLowerCase())
          );
          return textMatch ? (textMatch.en_value || textMatch.ar_value || answerData) : answerData;
        }
        return String(answerData);
      
      case 'single_choice':
        if (parsedOptions && answerData !== null && answerData !== undefined) {
          const option = findOptionById(answerData, parsedOptions);
          if (option) {
            return option.en_value || option.ar_value || String(answerData);
          }
          // Fallback: try direct string match
          const directMatch = parsedOptions.find((opt: any) => 
            opt.en_value === answerData || opt.ar_value === answerData
          );
          return directMatch ? (directMatch.en_value || directMatch.ar_value || String(answerData)) : String(answerData);
        }
        return String(answerData);
      
      case 'multiple_choice':
        if (Array.isArray(answerData) && answerData.length > 0) {
          if (parsedOptions && parsedOptions.length > 0) {
            const formattedAnswers = answerData.map((id: any) => {
              const option = findOptionById(id, parsedOptions);
              if (option) {
                return option.en_value || option.ar_value || String(id);
              }
              // Fallback: try direct string match
              const directMatch = parsedOptions.find((opt: any) => 
                opt.en_value === id || opt.ar_value === id
              );
              return directMatch ? (directMatch.en_value || directMatch.ar_value || String(id)) : String(id);
            }).filter((val: string) => val); // Remove empty values
            return formattedAnswers.length > 0 ? formattedAnswers.join(', ') : 'No options selected';
          }
          return answerData.join(', ');
        }
        return 'No options selected';
      
      case 'number':
        return String(answerData);
      
      case 'date':
        try {
          return new Date(answerData).toLocaleDateString();
        } catch {
          return String(answerData);
        }
      
      case 'text':
        return String(answerData);
      
      default:
        return String(answerData);
    }
  };

  const getAnswerIcon = (isAnswered: boolean, answerScore: number, questionValue: number) => {
    if (!isAnswered) {
      return <XCircle size={18} className="answer-icon not-answered" />;
    }
    const percentage = questionValue > 0 ? (answerScore / questionValue) * 100 : 0;
    if (percentage >= 80) {
      return <CheckCircle size={18} className="answer-icon answered-good" />;
    } else if (percentage >= 50) {
      return <AlertCircle size={18} className="answer-icon answered-medium" />;
    } else {
      return <XCircle size={18} className="answer-icon answered-poor" />;
    }
  };

  if (!data || !data.categories || data.categories.length === 0) {
    return (
      <div className="assessment-details-empty">
        <p>No assessment data available</p>
      </div>
    );
  }

  return (
    <div className="assessment-details-chart">
      {data.categories.map((category) => {
        const isExpanded = expandedCategories.has(category.categoryId);
        const answeredCount = category.questions.filter((q) => q.isAnswered).length;
        const totalCount = category.questions.length;
        const categoryScore = category.questions.reduce((sum, q) => sum + q.answerScore, 0);
        const categoryMax = category.questions.reduce((sum, q) => sum + q.questionValue, 0);
        const categoryPercentage = categoryMax > 0 ? Math.round((categoryScore / categoryMax) * 100) : 0;

        return (
          <div key={category.categoryId} className="category-section">
            <div
              className="category-header"
              onClick={() => toggleCategory(category.categoryId)}
            >
              <div className="category-header-left">
                {isExpanded ? (
                  <ChevronDown size={20} className="expand-icon" />
                ) : (
                  <ChevronRight size={20} className="expand-icon" />
                )}
                <h3 className="category-title">{category.categoryName}</h3>
              </div>
              <div className="category-stats">
                <span className="stat-badge">
                  {answeredCount} / {totalCount} answered
                </span>
                <span className="stat-badge score-badge">
                  {categoryPercentage}%
                </span>
              </div>
            </div>

            {isExpanded && (
              <div className="questions-container">
                {category.questions.map((question) => {
                  const isQuestionExpanded = expandedQuestions.has(question.questionId);
                  const answerPercentage = question.questionValue > 0
                    ? Math.round((question.answerScore / question.questionValue) * 100)
                    : 0;

                  return (
                    <div key={question.questionId} className="question-item">
                      <div
                        className="question-header"
                        onClick={() => toggleQuestion(question.questionId)}
                      >
                        <div className="question-header-left">
                          {isQuestionExpanded ? (
                            <ChevronDown size={16} className="expand-icon-small" />
                          ) : (
                            <ChevronRight size={16} className="expand-icon-small" />
                          )}
                          {getAnswerIcon(question.isAnswered, question.answerScore, question.questionValue)}
                          <span className="question-text">{question.questionText}</span>
                        </div>
                        <div className="question-meta">
                          {question.isAnswered && (
                            <span className="question-score">
                              Score: {question.answerScore} / {question.questionValue} ({answerPercentage}%)
                            </span>
                          )}
                          <span className="question-type-badge">{question.questionType}</span>
                        </div>
                      </div>

                      {isQuestionExpanded && (
                        <div className="answer-details">
                          <div className="answer-section">
                            <strong>Answer:</strong>
                            <div className="answer-value">
                              {formatAnswer(question.answerData, question.questionType, question.questionOptions)}
                            </div>
                            {/* Debug: Show available options for choice questions */}
                            {(() => {
                              const opts = typeof question.questionOptions === 'string' 
                                ? (() => { try { return JSON.parse(question.questionOptions); } catch { return []; } })()
                                : question.questionOptions;
                              return opts && Array.isArray(opts) && opts.length > 0 && 
                                     (question.questionType === 'multiple_choice' || question.questionType === 'single_choice') ? (
                                <div className="answer-debug" style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                                  <strong>Available Options:</strong> {opts.map((opt: any) => opt.en_value || opt.id).join(', ')}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          {question.answeredAt && (
                            <div className="answer-meta">
                              <strong>Answered on:</strong> {new Date(question.answeredAt).toLocaleString()}
                            </div>
                          )}
                          <div className="answer-meta">
                            <strong>Question Value:</strong> {question.questionValue} points
                          </div>
                          {question.isAnswered && (
                            <div className="answer-meta">
                              <strong>Score Earned:</strong> {question.answerScore} points ({answerPercentage}%)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssessmentDetailsChart;

